import { useToast } from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartNo, TExistingLineQuantity } from '../contexts/types';
import { WebAPIContext } from '../contexts/WebAPIContext';
import { buildExistingLineCapacityChartData, TExistingLineCapacitySource } from '../utils/existingLineCapacityChart';
import GoogleAPI, { GoogleAPIBatchCollector } from '../utils/GoogleAPI';
import {
  existingLineQuantityToSpreadSheetData,
  setupExistingLineQuantitySheet,
  spreadSheetDataToExistingLineQuantity,
} from './useExistingLineQuantitySpreadSheet';
import { SHEET_NAME_EXISTING_LINE_QUANTITY } from './useSpreadSheetSync';

type Params = {
  projectId: string;
  /** 振り返り日（null = 通常モード） */
  reviewDate: string | null;
  /** 表示範囲（量産開始〜EOL）の年月ラベル。シート書き出し・読込の範囲 */
  labels: string[];
  /** トーストのタイトル（画面名） */
  title: string;
  /** 画面の GoogleAPI インスタンス（サインイン状態を画面と共有する） */
  googleAPI: GoogleAPI;
  /** グラフの元データ（各画面の既存グラフの計算から切り出したもの）。未計算なら null */
  source: TExistingLineCapacitySource | null;
  /** 案件のPartNo一覧（表示用PartNoの解決） */
  capexPartNo: Pick<PartNo, "partNo" | "displayPartNo">[];
};

/**
 * 生産キャパグラフ（既存ライン含む）の画面側の状態・操作をまとめたフック
 * 新製品シミュレーション・生産性向上シミュレーション（増産のみ）で共通に使う
 * - 要否（ON/OFF）、既存ライン生産数量、入力モード（画面入力／GoogleSpread）
 * - 既存ライン生産数量の読込（案件・振り返り切替時）・保存（事業部確認時のみ）
 * - グラフデータ（当初計画／ダウンサイド／保存済み設定）の組み立て
 */
export const useExistingLineCapacity = ({ projectId, reviewDate, labels, title, googleAPI, source, capexPartNo }: Params) => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  // 生産キャパグラフの要否（ON/OFF）。ON/OFF状態自体は保存せず、保存値で判定する
  // ONで事業部確認すると配列（未入力なら []）、OFFなら null で保存される → 読込時は null 以外ならON
  const [show, setShow] = useState<boolean>(false);
  // 既存ラインPartNoの生産予定数量（手入力分。ダウンサイド・改善シナリオの影響を受けない）
  const [data, setData] = useState<TExistingLineQuantity>([]);
  // 生産キャパグラフ(当初計画)
  const [chartDataOriginal, setChartDataOriginal] = useState<any>(null);
  // 生産キャパグラフ(ダウンサイド)
  const [chartDataModified, setChartDataModified] = useState<any>(null);
  // 生産キャパグラフ(保存済み設定用)配列形式
  const [chartDataForSavedConfig, setChartDataForSavedConfig] = useState<{ configId: string; data: any }[]>([]);

  // 既存ライン生産数量の入力モード（シミュレーション設定とは別枠）
  const [inputMode, setInputMode] = useState<"google" | "manual">("manual");
  const [spreadSheetWindow, setSpreadSheetWindow] = useState<Window | null>(null);
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [syncError, setSyncError] = useState<{ invalidValueErrors: string[] }>({ invalidValueErrors: [] });

  // アンマウント時（画面遷移等）に生産キャパのGoogleSpreadを閉じる（シミュレーション設定のGoogleSpreadと同じ対応。cleanupで最新の参照を使うためrefに保持）
  const spreadSheetWindowRef = useRef<Window | null>(null);
  useEffect(() => {
    spreadSheetWindowRef.current = spreadSheetWindow;
  }, [spreadSheetWindow]);
  useEffect(() => {
    return () => {
      if (spreadSheetWindowRef.current && !spreadSheetWindowRef.current.closed) {
        spreadSheetWindowRef.current.close();
      }
    };
  }, []);

  /**
   * 「画面で入力」ボタン押下
   */
  const handleManualClick = () => {
    spreadSheetWindow?.close();
    setSpreadSheetWindow(null);
    setInputMode("manual");
    setSyncError({ invalidValueErrors: [] });
  };

  /**
   * 「GoogleSpread」ボタン押下
   * シミュレーション設定とは別のSpreadSheetを作成し、既存ライン生産数量を書き込む
   */
  const handleGoogleSpreadClick = async () => {
    if (inputMode === "google") {
      return;
    }

    // エラー時に今回開いたタブを閉じるため保持（state の spreadSheetWindow はこの時点ではまだ設定されていない）
    let openedWindow: Window | null = null;

    try {
      setIsLoading(true);
      setInputMode("google");

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");
      openedWindow = newWindow;
      // 作成途中で画面遷移した場合もアンマウント時に閉じられるよう、先にrefへ保持する
      spreadSheetWindowRef.current = newWindow;

      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      const fileName = "ExistingLineQuantity";
      const folderPath = `CEAS/SpreadSheet/ExistingLineQuantity/${projectId}`;

      let folderId = await googleAPI.createFolderPath(folderPath, null);
      if (!folderId) { folderId = ""; }

      const newSpreadsheetId = await googleAPI.createSpreadSheet(fileName, folderId, null);
      setSpreadsheetId(newSpreadsheetId);

      if (newSpreadsheetId) {
        // シートを追加（デフォルトシートは自動削除される）
        const sheetNameToId = await googleAPI.addSheets(newSpreadsheetId, [SHEET_NAME_EXISTING_LINE_QUANTITY]);
        const sheetId = sheetNameToId[SHEET_NAME_EXISTING_LINE_QUANTITY] ?? 0;

        // 表示範囲（量産開始〜EOL）で書き出す
        const batch = new GoogleAPIBatchCollector(newSpreadsheetId);
        await batch.clear(newSpreadsheetId, SHEET_NAME_EXISTING_LINE_QUANTITY);
        await batch.spreadSheetSetValue(newSpreadsheetId, SHEET_NAME_EXISTING_LINE_QUANTITY, 1, 1, existingLineQuantityToSpreadSheetData(data, labels));
        await setupExistingLineQuantitySheet(batch, newSpreadsheetId, sheetId, labels.length);
        await batch.flush();

        if (newWindow) {
          newWindow.location.href = googleAPI.getOpenSpradSheetUrl(newSpreadsheetId);
          setSpreadSheetWindow(newWindow);

          // SpreadSheetウィンドウを手動で閉じられた場合は画面入力に戻す
          const interval = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(interval);
              setSpreadSheetWindow(null);
              setInputMode("manual");
            }
          }, 500);
        }
      }

    } catch (error) {
      console.error("Change GoogleSpread Error (ExistingLineQuantity).", error);
      openedWindow?.close();
      setSpreadSheetWindow(null);
      setInputMode("manual");

      toast({
        title,
        description: t('features.InvestmentScreening.SimulationNewProduct.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 「データ連携」ボタン押下
   * 既存ライン生産数量シートを読み込んで画面に反映する（表示範囲外の既存値は保持）
   */
  const handleSyncData = async () => {
    if (!spreadsheetId || inputMode !== "google") {
      return;
    }

    try {
      setIsLoading(true);

      // トークンの有効性を確認
      if (!await googleAPI.ensureValidToken()) {
        googleAPI.signIn();
      }

      const result = await googleAPI.spreadSheetGetValues(spreadsheetId, `${SHEET_NAME_EXISTING_LINE_QUANTITY}!A1:ZZ`);
      const { data: syncedData, errors } = spreadSheetDataToExistingLineQuantity(
        result.values || [],
        labels,
        data,
      );
      setSyncError({ invalidValueErrors: errors });
      setData(syncedData);

      toast({
        title,
        description: errors.length > 0
          ? t('features.InvestmentScreening.SimulationNewProduct.message.existingLineSyncPartialFailed')
          : t('features.InvestmentScreening.SimulationNewProduct.message.dataSyncCompleted'),
        status: errors.length > 0 ? 'warning' : 'success',
        duration: errors.length > 0 ? 5000 : 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error("Sync Data Error (ExistingLineQuantity).", error);
      toast({
        title,
        description: t('features.InvestmentScreening.SimulationNewProduct.message.dataSyncFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 既存ライン生産数量を保存（案件単位。シナリオ保存とは独立）
   * 他グラフと同じく「事業部確認」の保存時にのみ呼ぶ（入力・データ連携の時点では保存しない）
   * ONなら PartNo が空の行（入力途中）を除いて保存、OFFなら null（保存データを削除）
   * @returns true：処理成功、false：処理失敗
   */
  const save = async (): Promise<boolean> => {
    const saveData = show ? JSON.stringify(data.filter(row => row.partNo.trim() !== "")) : null;
    try {
      const result = await WebAPI.request({
        query: `
          mutation putExistingLineQuantity($input: ExistingLineQuantityInput!) {
            putExistingLineQuantity(input: $input) {status}
          }
        `,
        variables: {
          input: {
            projectId,
            existingLineQuantityData: saveData,
            ...(reviewDate && { reviewDate }),
          }
        }
      });
      if (result?.data?.putExistingLineQuantity?.status !== "success") {
        throw new Error(JSON.stringify(result));
      }
      return true;
    } catch (error) {
      console.error("Failed to call API putExistingLineQuantity.", error);
      toast({
        title,
        description: t('features.InvestmentScreening.SimulationNewProduct.message.existingLineQuantitySaveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  /**
   * 既存ライン生産数量の読込（案件・振り返り切替時）
   */
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await WebAPI.request({
          query: `
            query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
              getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
                projectId
                existingLineQuantityData
              }
            }
          `,
          variables: { projectId, ...(reviewDate && { reviewDate }) }
        });
        if (response?.errors) {
          // 他のトーストと同じくタイトルは画面名
          console.error(`Failed to call API getProjectAdditionalInfoById.errors = ${JSON.stringify(response.errors)}`);
          toast({
            title,
            description: t('features.InvestmentScreening.SimulationNewProduct.message.existingLineCapacityFetchFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        // 未登録（null）・解析不能は空として扱う
        let loaded: TExistingLineQuantity = [];
        // ONで保存された（配列として読めた）かどうか。未入力のままONで保存した [] もONとして復元する
        let savedAsOn = false;
        const raw = response?.data?.getProjectAdditionalInfoById?.existingLineQuantityData;
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              loaded = parsed;
              savedAsOn = true;
            }
          } catch (e) {
            console.error("Failed to parse existingLineQuantityData:", e);
          }
        }
        if (!cancelled) {
          setData(loaded);
          // ONで保存されていれば初期表示ON（未入力でもON。申請書の表示有無と揃える）、null（OFFで保存・未登録）ならOFF
          setShow(savedAsOn);
        }
      } catch (error) {
        console.error("Failed to call API getProjectAdditionalInfoById (existingLineQuantityData).", error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reviewDate]);

  /**
   * 【生産キャパグラフ（当初計画 / ダウンサイド / 保存済み設定）】チャートデータ作成
   */
  useEffect(() => {
    if (!source) return;

    const { labels: sourceLabels, original, modified, savedConfigs } = source;
    const capacityLabel = t('features.InvestmentScreening.SimulationNewProduct.chart.productionCapacity');
    setChartDataOriginal(buildExistingLineCapacityChartData(sourceLabels, data, original, capexPartNo, capacityLabel));
    setChartDataModified(buildExistingLineCapacityChartData(sourceLabels, data, modified, capexPartNo, capacityLabel));
    setChartDataForSavedConfig(savedConfigs.map(version => ({
      configId: version.configId,
      data: buildExistingLineCapacityChartData(sourceLabels, data, version, capexPartNo, capacityLabel),
    })));
  }, [source, data, capexPartNo, t]);

  return {
    show, setShow,
    data, setData,
    inputMode, isLoading, syncError,
    handleManualClick, handleGoogleSpreadClick, handleSyncData,
    save,
    chartDataOriginal, chartDataModified, chartDataForSavedConfig,
  };
};

export type ExistingLineCapacityState = ReturnType<typeof useExistingLineCapacity>;
